import role5Schema from '../../../../assets/forms/role-120100.form.json';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  buildWarningBag,
  sheetHasTriggeredWarning,
  WarnableControl,
} from './warning-eval.helper';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { exceedsSchemaMaxLength } from '../../../utils/helpers';
import {
  evaluateRole5OtherPersonRequired,
  getRole5VisibleDirectorCount,
  ROLE5_IDENTIFICATION_PATTERN,
  role5TypeRequiresPattern,
} from './role5-statement-by-directors.rules';

/**
 * Pure validator for Role 5 (Statement by Directors).
 *
 * Mirrors the editor's role5-120100-statement-by-directors.component.ts
 * validation surface (i.e. what drives `statementByDirectorsForm.valid`):
 *
 *  - Static required fields from schema for controls that have NO
 *    `ui.directorNumber` AND NO `requiredIfAllVisibleDirectorsMatch`
 *    (these get `Validators.required` once in buildForm and never change).
 *  - Per-director (directorNumber 1..5): when visible
 *    (directorNumber <= NumberOfDirectorsSigningStatementByDirectors, default 2),
 *    fields flagged `control.required === true` in the schema get
 *    `Validators.required`. For IdentificationNumber fields, the 12-digit
 *    pattern is additionally applied when the companion Type field is one of
 *    {MyKad, MyPR, MyKAS} (case-insensitive).
 *  - The "Other person primarily responsible..." triplet: required under
 *    EITHER of two conditions — every visible director's
 *    DisclosureWhether...IsAlsoPrimarilyResponsible field equals "Not primarily
 *    responsible for financial management of the company"
 *    (`requiredIfAllVisibleDirectorsMatch`), OR any one of the three fields
 *    carries a value (`requiredIfAnyOfGroupFilled`). Both live in
 *    `evaluateRole5OtherPersonRequired`, shared with the editor. The pattern
 *    validator on the IdentificationNumber for this triplet applies under the
 *    same MyKad/MyPR/MyKAS rule as the per-director case.
 *
 * Notes on parity:
 *  - maxLength IS checked, against the schema's declared cap, via the shared
 *    `exceedsSchemaMaxLength` the editor also seats — so an over-length value
 *    (which only an API load or a cross-tab adoption can produce, the rendered
 *    `maxlength` attribute capping anything typed or pasted) blocks in both
 *    places rather than in the editor alone.
 *  - Warnings (dateComparison under `control.warnings`) are evaluated via
 *    FieldWarningService and never flip `form.valid`, so they are out
 *    of scope for this validator (which only mirrors `valid`).
 */

interface Role5PatternValidator {
  name?: string;
  pattern?: string;
  conditionalOn?: {
    qname: string;
    values: ReadonlyArray<string>;
  };
}

interface Role5SchemaControl {
  qname: string;
  required?: boolean;
  ui?: { directorNumber?: number };
  validators?: ReadonlyArray<Role5PatternValidator | { name?: string }>;
  requiredIfAllVisibleDirectorsMatch?: {
    directorCountField: string;
    directorFields: ReadonlyArray<string>;
    equals: string;
  };
  requiredIfAnyOfGroupFilled?: ReadonlyArray<string>;
}

const SCHEMA_CONTROLS: ReadonlyArray<Role5SchemaControl> =
  role5Schema.controls as Role5SchemaControl[];

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Look up the conditionalOn-qualified pattern validator attached to a given
 * IdentificationNumber control. Mirrors the component's buildIdentificationFieldMappings
 * which scans schema controls for a pattern validator carrying conditionalOn.
 */
function findConditionalPattern(
  control: Role5SchemaControl
): { typeField: string; values: ReadonlyArray<string> } | null {
  const patternValidator = control.validators?.find(
    (validator): validator is Role5PatternValidator =>
      (validator as Role5PatternValidator).name === 'pattern' &&
      !!(validator as Role5PatternValidator).conditionalOn
  );
  if (!patternValidator?.conditionalOn) return null;
  const { qname, values } = patternValidator.conditionalOn;
  if (!qname || !values) return null;
  return { typeField: qname, values };
}

function checkStaticAndDirectorErrors(
  sheetData: Record<string, any>,
  visibleDirectorCount: number
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const control of SCHEMA_CONTROLS) {
    // requiredIfAllVisibleDirectorsMatch controls are handled separately.
    if (control.requiredIfAllVisibleDirectorsMatch) continue;

    const directorNumber = control.ui?.directorNumber;

    if (directorNumber === undefined) {
      // Non-director control — static required straight from the schema
      // (set once in buildForm and never mutated thereafter).
      if (control.required === true && isEmpty(sheetData[control.qname])) {
        errors[control.qname] = `${control.qname} is required`;
      } else if (
        exceedsSchemaMaxLength(sheetData[control.qname], control.qname, role5Schema)
      ) {
        errors[control.qname] = `${control.qname} exceeds its maximum length`;
      }
      continue;
    }

    // Per-director control — only visible directors are validated. The
    // component's updateDirectorFieldValidators clears validators entirely
    // for hidden directors, so hidden directors contribute nothing to
    // form.valid even if they happen to hold stale data.
    const isVisible = directorNumber <= visibleDirectorCount;
    if (!isVisible) continue;

    if (control.required === true && isEmpty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
      continue;
    }

    // Schema-declared 255-character ceiling on text controls, scoped by the
    // same visibility rule as `required`.
    if (exceedsSchemaMaxLength(sheetData[control.qname], control.qname, role5Schema)) {
      errors[control.qname] = `${control.qname} exceeds its maximum length`;
      continue;
    }

    // Conditional 12-digit pattern on IdentificationNumber fields.
    const conditionalPattern = findConditionalPattern(control);
    if (conditionalPattern) {
      const typeValue = sheetData[conditionalPattern.typeField];
      if (role5TypeRequiresPattern(typeValue, conditionalPattern.values)) {
        const idValue = sheetData[control.qname];
        if (
          typeof idValue === 'string' &&
          idValue.length > 0 &&
          !ROLE5_IDENTIFICATION_PATTERN.test(idValue)
        ) {
          errors[control.qname] = `${control.qname} must be a 12-digit number`;
        }
      }
    }
  }

  return errors;
}

function checkRequiredIfAllVisibleDirectorsMatchErrors(
  sheetData: Record<string, any>
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const control of SCHEMA_CONTROLS) {
    if (!control.requiredIfAllVisibleDirectorsMatch) continue;

    const config = control.requiredIfAllVisibleDirectorsMatch;
    // Required under EITHER the all-visible-directors-disclaim rule OR the
    // any-one-of-the-trio-filled rule — see evaluateRole5OtherPersonRequired.
    const isRequired = evaluateRole5OtherPersonRequired(
      sheetData,
      config,
      control.requiredIfAnyOfGroupFilled ?? []
    );

    if (isRequired && isEmpty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
    } else if (
      exceedsSchemaMaxLength(sheetData[control.qname], control.qname, role5Schema)
    ) {
      errors[control.qname] = `${control.qname} exceeds its maximum length`;
    }

    // Pattern validator (conditionalOn Type field) — applied in BOTH the
    // required and the non-required branch of updateConditionalFieldsVisibility
    // whenever the type is MyKad/MyPR/MyKAS, so we mirror that here
    // regardless of the requiredIf... outcome.
    const conditionalPattern = findConditionalPattern(control);
    if (conditionalPattern) {
      const typeValue = sheetData[conditionalPattern.typeField];
      if (role5TypeRequiresPattern(typeValue, conditionalPattern.values)) {
        const idValue = sheetData[control.qname];
        if (
          typeof idValue === 'string' &&
          idValue.length > 0 &&
          !ROLE5_IDENTIFICATION_PATTERN.test(idValue)
        ) {
          errors[control.qname] = `${control.qname} must be a 12-digit number`;
        }
      }
    }
  }

  return errors;
}

export const role5StatementByDirectorsValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.statementByDirectors,
  // Tier 1 by primary characteristic (required-field driven). Director
  // visibility and requiredIfAllVisibleDirectorsMatch are deterministic
  // projections from same-sheet fields, not cross-sheet rules, so Tier 1
  // remains the correct classification for the parity harness.
  tier: 1,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const visibleDirectorCount = getRole5VisibleDirectorCount(sheetData);
    const errors: Record<string, string> = {
      ...checkStaticAndDirectorErrors(sheetData, visibleDirectorCount),
      ...checkRequiredIfAllVisibleDirectorsMatchErrors(sheetData),
    };
    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: sheetHasTriggeredWarning(
        role5Schema.controls as unknown as WarnableControl[],
        sheetData,
        buildWarningBag(
          context.crossSheetData?.get(SHEET_KEYS.filingInfo) ??
            context.crossSheetData?.get(SHEET_KEYS.filingPrerequisites) ??
            {},
          sheetData,
          // Statement-by-directors compares its signing date against scopeOfFiling
          // (approval date); nest the cross-sheet map + own key so it resolves.
          { sheets: context.crossSheetData, sheetKey: SHEET_KEYS.statementByDirectors }
        )
      ),
      errors,
    };
  },
};
